import { useState } from 'react'
import type { FacetValue, FacetsResponse } from '../../services/analyticsService'
import { DIMENSION_LABELS, type Dimension } from '../../utils/exploreFilters'

interface FacetPanelProps {
  data: FacetsResponse | undefined
  isLoading: boolean
  isError: boolean
  dimensions: Dimension[]
  selected: Partial<Record<Dimension, string[]>>
  onToggle: (dimension: Dimension, value: string) => void
  formatValue: (dimension: Dimension, value: string) => string
}

interface FacetRow {
  value: string
  events: number | null
  sessions: number | null
}

/**
 * Selected values always appear, even when they fall outside the top N the
 * server returned -- otherwise a value selected from a shared URL could be
 * active but invisible, with no way to deselect it here.
 */
function rowsFor(values: FacetValue[] | undefined, selectedValues: string[]): FacetRow[] {
  const rows: FacetRow[] = (values ?? []).map(item => ({ value: item.value, events: item.events, sessions: item.sessions }))
  for (const value of selectedValues) {
    if (!rows.some(row => row.value === value)) rows.push({ value, events: null, sessions: null })
  }
  return rows
}

export default function FacetPanel({
  data,
  isLoading,
  isError,
  dimensions,
  selected,
  onToggle,
  formatValue,
}: FacetPanelProps) {
  const [open, setOpen] = useState(true)

  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-3 text-left hover:bg-white/[0.02]"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          Breakdown
          {data && (
            <span className="ml-3 font-normal normal-case tracking-normal text-gray-500">
              {data.total.events.toLocaleString()} matching event{data.total.events === 1 ? '' : 's'} ·{' '}
              {data.total.sessions.toLocaleString()} session{data.total.sessions === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span className="text-xs text-gray-500">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-5 pb-5">
          {isError ? (
            <p className="rounded-2xl border border-rose-400/20 bg-rose-500/[0.06] p-4 text-sm text-rose-200">
              Could not load the breakdown. The event table below is unaffected.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {dimensions.map(dimension => (
                <FacetCard
                  key={dimension}
                  dimension={dimension}
                  rows={rowsFor(data?.facets[dimension], selected[dimension] ?? [])}
                  selectedValues={selected[dimension] ?? []}
                  isLoading={isLoading && !data}
                  onToggle={value => onToggle(dimension, value)}
                  formatValue={value => formatValue(dimension, value)}
                />
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-600">
            Click a value to filter. Counts in each box ignore that box's own selection, so the alternatives stay visible.
          </p>
        </div>
      )}
    </div>
  )
}

function FacetCard({
  dimension,
  rows,
  selectedValues,
  isLoading,
  onToggle,
  formatValue,
}: {
  dimension: Dimension
  rows: FacetRow[]
  selectedValues: string[]
  isLoading: boolean
  onToggle: (value: string) => void
  formatValue: (value: string) => string
}) {
  const max = Math.max(1, ...rows.map(row => row.events ?? 0))

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.025] p-3">
      <h3 className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
        <span>{DIMENSION_LABELS[dimension]}</span>
        {selectedValues.length > 0 && <span className="text-indigo-300">{selectedValues.length} selected</span>}
      </h3>

      {isLoading ? (
        <div className="space-y-1.5" aria-hidden>
          {[0, 1, 2].map(index => (
            <div key={index} className="h-7 animate-pulse rounded-lg bg-white/[0.04]" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="px-1 py-2 text-xs text-gray-600">None in this range</p>
      ) : (
        <ul className="space-y-0.5">
          {rows.map(row => {
            const isSelected = selectedValues.includes(row.value)
            const label = formatValue(row.value)
            return (
              <li key={row.value}>
                <button
                  type="button"
                  onClick={() => onToggle(row.value)}
                  aria-pressed={isSelected}
                  title={
                    row.events === null
                      ? `${label} (selected, outside the top values)`
                      : `${label}: ${row.events.toLocaleString()} events, ${row.sessions?.toLocaleString()} sessions`
                  }
                  className={`relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${
                    isSelected ? 'bg-indigo-500/15 text-white ring-1 ring-indigo-400/40' : 'text-gray-300 hover:bg-white/[0.05]'
                  }`}
                >
                  {row.events !== null && (
                    <span
                      aria-hidden
                      className={`absolute inset-y-0 left-0 ${isSelected ? 'bg-indigo-400/15' : 'bg-white/[0.05]'}`}
                      style={{ width: `${(row.events / max) * 100}%` }}
                    />
                  )}
                  <span className="relative min-w-0 truncate">{label}</span>
                  <span className="relative shrink-0 tabular-nums text-gray-500">
                    {row.events === null ? '—' : row.events.toLocaleString()}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
